import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { toValidRegion } from '@/helpers/toValidRegion';
import { type Dispatch } from '@/store';

/**
 * Hook to set the user's region from their auth token claims.
 *
 * The claim is normalised through the shared `toValidRegion` rather than
 * compared literally, and the shared part is the point: this hook is the
 * writer that sets `auth.userRegion` from the token claim, and `getRegion()`
 * reads that field ABOVE the raw claim. Normalising on the read side alone
 * would leave this write silently rejecting the very value the read then
 * accepts — the same claim admitted by one path and dropped by the other.
 *
 * It is not the only `setUserRegion` dispatch site: the country, query-param,
 * cookie and EHR-launch paths each write the same field, and each still
 * normalises on its own terms (`useEpicLaunch` upper-cases without trimming).
 * Bringing those onto this helper belongs with the call-site sweep, not here.
 *
 * Nothing is persisted when the claim is absent or is not a region in any
 * casing; the caller's region then resolves from the rest of the chain.
 */
export function useSetUserRegionFromToken(userRegion: string | undefined) {
  const dispatch = useDispatch<Dispatch>();

  useEffect(() => {
    const region = toValidRegion(userRegion);
    if (region) {
      dispatch.auth.setUserRegion(region);
    }
  }, [dispatch.auth, userRegion]);
}
